/* =========================================================
   Web push — Android and Windows (Chrome, Edge, Firefox). Not iOS: Safari
   only allows this from a home-screen-installed PWA, a different flow
   this does not attempt — isSupported() is false there and everything
   below quietly does nothing.

   The service worker is served by the app itself (GET /circle/push-sw.js
   via the App Proxy), not shipped as a theme asset — a service worker can
   only be registered from the same origin as the page registering it,
   and theme assets are served from cdn.shopify.com, a different origin
   than the storefront. The App Proxy is the one place this app has a URL
   on yourvro.com itself. See src/lib/pushServiceWorker.js.

   Exposes window.VroPush = { isSupported, subscribe, unsubscribe,
   isSubscribed }. Depends on window.VroCircleApi (assets/vro-circle-
   api.js) for the signed App Proxy calls, so this loads after it.
   ========================================================= */
(function () {
  'use strict';

  var SW_URL = '/apps/vro/circle/push-sw.js';
  var PROMPTED_KEY = 'vro_push_prompted';

  function isSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window && window.isSecureContext;
  }

  /* A VAPID public key arrives as base64url; the Push API wants the same
     bytes as a Uint8Array. This is the standard conversion — nothing
     Circle-specific about it. */
  function urlBase64ToUint8Array(base64) {
    var padding = '='.repeat((4 - (base64.length % 4)) % 4);
    var b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
    var raw = atob(b64);
    var out = new Uint8Array(raw.length);
    for (var i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
  }

  /* Whether the merchant has push turned on (Admin → Circle → Push
     notifications), cached for the page's lifetime. Asked fresh here
     rather than read off whatever the-circle.js's own /me call cached:
     this file is also loaded on the standalone voter flow
     (circle-take.js), which never runs the-circle.js at all, so it needs
     its own answer rather than depending on a variable that page doesn't
     have. Fails open on a network error — a merchant's kill switch not
     answering shouldn't be the reason a working subscribe silently does
     nothing; the three send points on the server enforce the real off
     switch regardless. */
  var pushEnabledPromise = null;
  function pushEnabled() {
    var Api = window.VroCircleApi;
    if (!Api) return Promise.resolve(true);
    if (!pushEnabledPromise) {
      pushEnabledPromise = Api.api('/me')
        .then(function (me) { return !me || !me.limits || me.limits.pushEnabled !== false; })
        .catch(function () { return true; });
    }
    return pushEnabledPromise;
  }

  var registrationPromise = null;
  function registration() {
    if (!isSupported()) return Promise.resolve(null);
    if (!registrationPromise) {
      registrationPromise = navigator.serviceWorker.register(SW_URL).catch(function () { return null; });
    }
    return registrationPromise;
  }

  function isSubscribed() {
    return registration().then(function (reg) {
      if (!reg) return false;
      return reg.pushManager.getSubscription().then(function (sub) { return !!sub; });
    });
  }

  /**
   * Turns on push for this device, if it will have it.
   *
   * `opts.silent` skips the browser's own permission prompt — used by the
   * automatic call sites (after sharing a list, after casting a take) so
   * a customer who already said no isn't asked again on every action;
   * `Notification.permission` already carries that answer, this just
   * reads it instead of re-asking. A visible "Turn on notifications"
   * control the customer taps on purpose should call subscribe() without
   * this, so it always shows the real prompt.
   *
   * Resolves true/false. Never rejects — a customer declining, or a
   * browser with no push support, is an ordinary outcome for every
   * caller here, not a failure worth an error handler.
   */
  function subscribe(opts) {
    opts = opts || {};
    var Api = window.VroCircleApi;
    if (!isSupported() || !Api || Api.isGuest()) return Promise.resolve(false);

    if (opts.silent && Notification.permission !== 'granted') return Promise.resolve(false);

    return pushEnabled().then(function (enabled) {
      if (!enabled) return false;
      return doSubscribe(opts);
    });
  }

  function doSubscribe(opts) {
    var Api = window.VroCircleApi;
    return registration().then(function (reg) {
      if (!reg) return false;
      return reg.pushManager.getSubscription().then(function (existing) {
        if (existing) return sendSubscription(existing);

        if (Notification.permission === 'denied') return false;
        if (opts.silent && Notification.permission !== 'granted') return false;

        return Api.api('/push/vapid-public-key').then(function (d) {
          return reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(d.key)
          });
        }).then(sendSubscription).catch(function () {
          // Notification.requestPermission was declined (surfaces as a
          // rejected subscribe()), or push isn't configured server-side
          // yet (503 from the vapid-public-key call) — both are "no push
          // today", not something to show the shopper.
          return false;
        });
      });
    });
  }

  function sendSubscription(sub) {
    var json = sub.toJSON();
    return window.VroCircleApi.api('/push/subscribe', {
      method: 'POST',
      body: { endpoint: json.endpoint, keys: json.keys }
    }).then(function () { return true; }).catch(function () { return false; });
  }

  function unsubscribe() {
    return registration().then(function (reg) {
      if (!reg) return false;
      return reg.pushManager.getSubscription().then(function (sub) {
        if (!sub) return false;
        var endpoint = sub.endpoint;
        return sub.unsubscribe().then(function () {
          return window.VroCircleApi.api('/push/unsubscribe', {
            method: 'POST', body: { endpoint: endpoint }
          }).catch(function () {});
        }).then(function () { return true; });
      });
    });
  }

  /* The automatic, ask-once entry point for the moments this is actually
     useful — right after sharing a list, right after giving a take. Reads
     the flag rather than checking Notification.permission alone, because
     'default' also covers someone who dismissed the browser's prompt
     without an explicit choice; asking again on their very next action
     would read as nagging. */
  function promptOnce() {
    if (!isSupported()) return;
    try {
      if (localStorage.getItem(PROMPTED_KEY)) {
        subscribe({ silent: true });
        return;
      }
      localStorage.setItem(PROMPTED_KEY, '1');
    } catch (e) { /* storage blocked — fall through and ask anyway */ }
    subscribe();
  }

  window.VroPush = {
    isSupported: isSupported,
    subscribe: subscribe,
    unsubscribe: unsubscribe,
    isSubscribed: isSubscribed,
    promptOnce: promptOnce
  };
})();
